import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import { Alert } from "react-bootstrap";
import { useEventParticipationSubmission } from "../../context/EventParticipationSubmissionManager";
import Loading from "../../components/Loading";
import EventParticipationVehicleImageUpload from "./EventParticipationVehicleImageUpload";
import UploadVehicleImageModal from "./UploadVehicleImageModal";
import EventTintButton from "../../components/EventTintButton";

function EventParticipationVehicleForm() {
    const { eventId } = useParams();
    const history = useHistory();
    const { formData, updateFormData, errors, event, loading } = useEventParticipationSubmission();

    // After refresh, state is wiped – redirect to first step to avoid submit errors
    useEffect(() => {
        if (loading) {
            return;
        }
        const hasNawData = formData.address?.trim();
        if (!hasNawData) {
            history.replace(`/event/${eventId}/participation-submission/naw`);
        }
    }, [formData.address, eventId, history, loading]);

    const [vehicles, setVehicles] = useState([]);
    const [loadingVehicles, setLoadingVehicles] = useState(true);
    const [error, setError] = useState(null);
    const [localErrors, setLocalErrors] = useState({});
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    const fetchVehicles = useCallback(async () => {
        setLoadingVehicles(true);
        setError(null);
        try {
            const response = await axios.post("/getUserVehicle");
            setVehicles(response.data.userVehicles);
        } catch (requestError) {
            console.error(requestError);
            setError("Er is iets fout gegaan bij het ophalen van uw voertuigen. Probeer het later opnieuw.");
        } finally {
            setLoadingVehicles(false);
        }
    }, []);

    useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    const handleVehicleSelect = useCallback((vehicle) => {
        setSelectedVehicle(vehicle);
        updateFormData("manufacturer", vehicle.manufacturer);
        updateFormData("model", vehicle.model);
        updateFormData("buildYear", vehicle.buildYear);
        updateFormData("licensePlate", vehicle.licensePlate);
        updateFormData("horsePower", vehicle.horsePower || "");
        if (vehicle.images && vehicle.images.length > 0 && vehicle.images[0].id) {
            updateFormData("vehicleImageId", vehicle.images[0].id);
            updateFormData("vehicleImageUrl", vehicle.images[0].url);
        } else {
            updateFormData("vehicleImageId", null);
            updateFormData("vehicleImageUrl", null);
        }
    }, [updateFormData]);

    const handleImageClick = useCallback((image) => {
        setSelectedImage(image);
        setShowImageModal(true);
    }, []);

    const handleCloseImageModal = useCallback(() => {
        setShowImageModal(false);
        setSelectedImage(null);
    }, []);

    const handleDeleteImage = useCallback(() => {
        updateFormData("vehicleImageId", null);
        updateFormData("vehicleImageUrl", null);
        setShowImageModal(false);
    }, [updateFormData]);

    const validateForm = useCallback(() => {
        const newErrors = {};

        if (!formData.manufacturer.trim()) {
            newErrors.manufacturer = "Merk is verplicht";
        }

        if (!formData.model.trim()) {
            newErrors.model = "Model is verplicht";
        }

        if (!formData.buildYear) {
            newErrors.buildYear = "Bouwjaar is verplicht";
        } else if (!/^\d{4}$/.test(formData.buildYear.toString())) {
            newErrors.buildYear = "Bouwjaar moet uit 4 cijfers bestaan";
        }

        if (!formData.licensePlate.trim()) {
            newErrors.licensePlate = "Kenteken is verplicht";
        }

        if (!formData.vehicleImageId) {
            newErrors.vehicleImageId = "Foto is verplicht";
        }

        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);

    const handleSubmit = useCallback((event) => {
        event.preventDefault();

        if (validateForm()) {
            history.push(`/event/${eventId}/participation-submission/fields`);
        }
    }, [eventId, history, validateForm]);

    const handleChange = useCallback((event) => {
        const { name, value } = event.target;
        updateFormData(name, value);

        setSelectedVehicle(null);

        if (localErrors[name]) {
            setLocalErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    }, [localErrors, updateFormData]);

    return (
        
        <div className="card">
            <UploadVehicleImageModal
                showImageModal={showImageModal}
                image={selectedImage}
                handleCloseImageModal={handleCloseImageModal}
                handleDeleteImage={handleDeleteImage}
                formData={formData}
            />

            <div className="card-header">
                <h3 className="mb-0 fs-5">Voertuig gegevens</h3>
            </div>

            <div className="card-body">
                {error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : loadingVehicles || loading ? (
                    <Loading />
                ) : (
                    <form onSubmit={handleSubmit}>
                        {vehicles.length > 0 && (
                            <div className="mb-4">
                                <h5>Selecteer een voertuig</h5>
                                <div className="vehicle-cards row">
                                    {vehicles.map((vehicle) => (
                                        <div
                                            key={vehicle.id}
                                            className="col-md-6 col-lg-4 mb-3"
                                        >
                                            <div
                                                className="card h-100"
                                                onClick={() => handleVehicleSelect(vehicle)}
                                                style={{ 
                                                    cursor: "pointer", 
                                                    backgroundColor: selectedVehicle && selectedVehicle.id === vehicle.id ? "#f2f2f2" : "",
                                                    borderColor: selectedVehicle && selectedVehicle.id === vehicle.id ? "#fae33d" : ""
                                                }}
                                            >
                                                <div className="card-body">
                                                    <h5 className="card-title">{vehicle.manufacturer} {vehicle.model}</h5>
                                                    <div className="card-text">
                                                        <p className="mb-1"><strong>Bouwjaar:</strong> {vehicle.buildYear}</p>
                                                        <p className="mb-1"><strong>Kenteken:</strong> {vehicle.licensePlate}</p>
                                                        {vehicle.horsePower && (
                                                            <p className="mb-0"><strong>Vermogen:</strong> {vehicle.horsePower} pk</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="">
                                    <p className="text-muted">Klik op een voertuig om het te selecteren, of vul hieronder handmatig de gegevens in.</p>
                                </div>
                            </div>
                        )}

                        <h5>Voertuig gegevens</h5>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label htmlFor="manufacturer" className="form-label">Merk</label>
                                <input
                                    type="text"
                                    className={`form-control ${localErrors.manufacturer ? "is-invalid" : ""}`}
                                    id="manufacturer"
                                    name="manufacturer"
                                    value={formData.manufacturer}
                                    onChange={handleChange}
                                />
                                {localErrors.manufacturer && (
                                    <div className="invalid-feedback">{localErrors.manufacturer}</div>
                                )}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label htmlFor="model" className="form-label">Model</label>
                                <input
                                    type="text"
                                    className={`form-control ${localErrors.model ? "is-invalid" : ""}`}
                                    id="model"
                                    name="model"
                                    value={formData.model}
                                    onChange={handleChange}
                                />
                                {localErrors.model && (
                                    <div className="invalid-feedback">{localErrors.model}</div>
                                )}
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label htmlFor="buildYear" className="form-label">Bouwjaar</label>
                                <input
                                    type="text"
                                    className={`form-control ${localErrors.buildYear ? "is-invalid" : ""}`}
                                    id="buildYear"
                                    name="buildYear"
                                    value={formData.buildYear}
                                    onChange={handleChange}
                                    placeholder="bijv. 2021"
                                />
                                {localErrors.buildYear && (
                                    <div className="invalid-feedback">{localErrors.buildYear}</div>
                                )}
                            </div>
                            <div className="col-md-4 mb-3">
                                <label htmlFor="licensePlate" className="form-label">Kenteken</label>
                                <input
                                    type="text"
                                    className={`form-control ${localErrors.licensePlate ? "is-invalid" : ""}`}
                                    id="licensePlate"
                                    name="licensePlate"
                                    value={formData.licensePlate}
                                    onChange={handleChange}
                                />
                                {localErrors.licensePlate && (
                                    <div className="invalid-feedback">{localErrors.licensePlate}</div>
                                )}
                            </div>
                            <div className="col-md-4 mb-3">
                                <label htmlFor="horsePower" className="form-label">Vermogen (pk)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="horsePower"
                                    name="horsePower"
                                    value={formData.horsePower}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="mt-4">
                            {localErrors.vehicleImageId && (
                                <div className="alert alert-danger">{localErrors.vehicleImageId}</div>
                            )}
                            <div className="d-flex flex-row">
                                <h5>Voertuig foto&apos;s</h5>
                                <span className="text-muted ml-2">(max 12MB)</span>
                            </div>
                            <p className="text-muted mb-3">
                                Voeg foto&apos;s toe van je voertuig om de organisatie een beter beeld te geven.
                            </p>
                            
                            {formData.vehicleImageUrl && (
                                <div className="mb-3">
                                    <h6>Geselecteerde foto:</h6>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="position-relative">
                                                <img 
                                                    src={formData.vehicleImageUrl} 
                                                    alt="Voertuig" 
                                                    className="img-fluid rounded" 
                                                    style={{ maxHeight: "200px", objectFit: "cover", cursor: "pointer" }}
                                                    onClick={() => handleImageClick(formData.vehicleImageUrl)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <EventParticipationVehicleImageUpload />
                        </div>

                        {errors.submit && (
                            <div className="alert alert-danger">{errors.submit}</div>
                        )}

                        <div className="d-flex justify-content-between mt-4">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => history.push(`/event/${eventId}/participation-submission/naw`)}
                            >
                                Terug
                            </button>
                            <EventTintButton
                                event={event}
                                onClick={handleSubmit}
                            >
                                Volgende
                            </EventTintButton>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default EventParticipationVehicleForm;
